// this is to trigger CI
import { type RoomDto } from "@crowdcoursing/api/openapi";
import { type AnyAction, createSlice, isAnyOf, type PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { type NullSanitized } from "~utils/sanitizeDto";

import { saveEventPicture } from "../actions/events";
import { onPayRoom } from "../actions/payments";
import {
  clearSelectedPod,
  deletePicture,
  getSelectedPod,
  savePicture,
  setSelectedPod as setSelectedPodAction,
} from "../actions/pods";
import { type ChatMember } from "../hooks/useChat";

/** @deprecated We are phasing out `redux` */
export type PageMenuContentType = "app-navigation" | "chat-members";

/** @deprecated We are phasing out `redux` */
interface DefaultPageDialogContentState {
  contentType: "unknown";
  contentProps: Record<string, unknown>;
}

/** @deprecated We are phasing out `redux` */
export interface DeletePodDialogContentState {
  contentType: "delete-pod";
  contentProps: {
    roomId: string;
    podName: string;
  };
}

/** @deprecated We are phasing out `redux` */
export interface DeleteOrganizationDialogContentState {
  contentType: "delete-organization";
  contentProps: {
    organizationId: string;
    organizationName: string;
    onDelete?: () => void;
  };
}

/** @deprecated We are phasing out `redux` */
export interface DeleteSessionDialogContentState {
  contentType: "delete-session";
  contentProps: {
    roomId: string;
    eventId: string;
    eventName: string;
  };
}

/** @deprecated We are phasing out `redux` */
export interface PodPaymentWizardDialogContentState {
  contentType: "pod-payment-wizard";
  contentProps: Record<string, unknown>;
}

/** @deprecated We are phasing out `redux` */
export type PageDialogContentState =
  | DefaultPageDialogContentState
  | DeletePodDialogContentState
  | DeleteSessionDialogContentState
  | PodPaymentWizardDialogContentState
  | DeleteOrganizationDialogContentState;

/** @deprecated We are phasing out `redux` */
export interface AppState {
  // todo: make chat reducer; add chatMembers, selectedPod, sessionId to chat reducer;
  chatMembers: ChatMember[];
  isLoading: number;
  isPageDialogVisible: boolean;
  isPageMenuVisible: boolean;
  pageDialogContentState: PageDialogContentState;
  pageMenuContentType: PageMenuContentType;
  selectedPod?: NullSanitized<RoomDto>;
  sessionId?: string;
  /**
   * suffixes for room images to bust caches so changes take effect instantly
   * for the user
   * */
  roomImageUrlCacheBusters: Record<string, string | undefined>;
  organizationImageUrlCacheBusters: Record<string, string | undefined>;
  bubbleImageUrlCacheBusters: Record<string, string | undefined>;
  eventImageUrlCacheBusters: Record<string, string | undefined>;
  /** what room are we going to pay for? Controls where we get redirected after the payment flow. */
  paymentRoomId?: string;
}

/** @deprecated We are phasing out `redux` */
const initialState: AppState = {
  bubbleImageUrlCacheBusters: {},
  chatMembers: [],
  eventImageUrlCacheBusters: {},
  isLoading: 0,
  isPageDialogVisible: false,
  isPageMenuVisible: false,
  organizationImageUrlCacheBusters: {},
  pageDialogContentState: {
    contentProps: {},
    contentType: "unknown",
  },
  pageMenuContentType: "app-navigation",
  roomImageUrlCacheBusters: {},
  selectedPod: undefined,
  sessionId: undefined,
};

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(savePicture.fulfilled, (state, action) => {
        state.roomImageUrlCacheBusters[action.payload.roomId] = new Date().getTime().toString();
      })
      .addCase(deletePicture.fulfilled, (state, action) => {
        state.roomImageUrlCacheBusters[action.payload.roomId] = new Date().getTime().toString();
      })
      .addCase(saveEventPicture.fulfilled, (state, action) => {
        state.eventImageUrlCacheBusters[action.payload.eventId] = new Date().getTime().toString();
      })
      .addCase(onPayRoom.pending, (state, action) => {
        state.paymentRoomId = action.meta.arg.roomId;
      })
      .addCase(getSelectedPod.fulfilled, (state, action) => {
        // @ts-expect-error Complains about `Date | undefined` not being assignable to `string | undefined`
        state.selectedPod = action.payload;
      })
      .addMatcher(
        (action: AnyAction) => action.type.endsWith("pending"),
        (state: AppState) => {
          state.isLoading = state.isLoading + 1;
        },
      )
      .addMatcher(
        (action: AnyAction) => action.type.endsWith("fulfilled") || action.type.endsWith("rejected"),
        (state: AppState) => {
          if (state.isLoading > 0) {
            state.isLoading = state.isLoading - 1;
          }
        },
      )
      .addMatcher(isAnyOf(clearSelectedPod.fulfilled), (state: AppState) => {
        state.selectedPod = undefined;
      })
      .addMatcher(isAnyOf(setSelectedPodAction.fulfilled), (state: AppState, action: PayloadAction<RoomDto>) => {
        // @ts-expect-error Complains about `Date | undefined` not being assignable to `string | undefined`
        state.selectedPod = action.payload;
      }),
  initialState,
  name: "app",
  reducers: {
    pageDialogClose: (state: AppState) => {
      state.isPageDialogVisible = false;
    },
    pageDialogOpen: (state: AppState, action: PayloadAction<PageDialogContentState>) => {
      state.isPageDialogVisible = true;
      state.pageDialogContentState = {
        ...action.payload,
      };
    },
    pageMenuClose: (state: AppState) => {
      state.isPageMenuVisible = false;
    },
    pageMenuOpen: (state: AppState, action: PayloadAction<PageMenuContentType | undefined>) => {
      state.isPageMenuVisible = true;
      state.pageMenuContentType = action.payload ?? "app-navigation";
    },
    setChatMembers: (state: AppState, action: PayloadAction<ChatMember[]>) => {
      state.chatMembers = action.payload;
    },
    setSelectedPod: (state: AppState, action: PayloadAction<RoomDto>) => {
      // @ts-expect-error Complains about `Date | undefined` not being assignable to `string | undefined`
      state.selectedPod = action.payload;
    },
    setSessionId: (state: AppState, action: PayloadAction<string | undefined>) => {
      state.sessionId = action.payload;
    },
  },
});

/** @deprecated We are phasing out `redux` */
export const {
  /** @deprecated We are phasing out `redux` */
  pageDialogClose,
  /** @deprecated We are phasing out `redux` */
  pageDialogOpen,
  /** @deprecated We are phasing out `redux` */
  pageMenuClose,
  /** @deprecated We are phasing out `redux` */
  pageMenuOpen,
  /** @deprecated We are phasing out `redux` */
  setChatMembers,
  /** @deprecated We are phasing out `redux` */
  setSelectedPod,
  /** @deprecated We are phasing out `redux` */
  setSessionId,
} = slice.actions;

/** @deprecated We are phasing out `redux` */
export default persistReducer(
  {
    // always use values from initialState, not localStorage
    blacklist: ["isLoading", "roomImageUrlCacheBusters"],
    key: slice.name,
    storage,
  },
  slice.reducer,
);
